import { useState, useEffect } from "react";
import { BlockButton, Icon, IconDefinitions, ODataGrid, TitlelessCard, LiveBadge, ResponsiveGridColumn, Breakpoints } from "../../components";
import CTEyeService from "../../services/CTEyeService";
import { UrlService } from "../../services";
import { useParams } from "react-router-dom";
import { GridCellProps, GridRowClickEvent } from "@progress/kendo-react-grid";
import { formatDateTime } from "../../app/dateHelpers";
import { usePageActions } from "../PageContent";
import { showErrorMessage, showSuccessMessage } from "../../app/tools";
import { HeartbeatModel, HeartbeatPayloadDetailed } from "../../data/entities";
import {  HeartbeatDetailsPanel } from "./components";
import { POLL_HEARTBEAT_LIST_MS } from "./helper/pollingConfig";

export function CTEyeClientHeartbeats() {
  const urlParams = useParams();
  const id = UrlService.getParamValue("id", urlParams) ?? "0";
  const service = new CTEyeService();

  const [latestHeartbeat, setLatestHeartbeat] = useState<HeartbeatModel | null>(null);
  const [selectedHeartbeat, setSelectedHeartbeat] = useState<HeartbeatModel | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [showSpinner, setShowSpinner] = useState(false);
  
  useEffect(() => {
    const interval = setInterval(() => {
      setRefreshKey((prev) => prev + 1);
      loadLatestHeartbeat();
    }, POLL_HEARTBEAT_LIST_MS);

    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    loadLatestHeartbeat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadLatestHeartbeat = async () => {
    try {
      const response = await service.getClientHeartbeatsOData(
        id,
        "$top=1&$orderby=modifiedDate desc",
      );
      if (response?.value && response.value.length > 0) {
        setLatestHeartbeat(response.value[0]);
      }
    
    } catch (error) {
      console.error("Error loading latest heartbeat:", error);
    }
  };

  const getData = (oDataQuery: string) => {
    return service.getClientHeartbeatsOData(id, oDataQuery);
  };

  const createdDateCell = (props: GridCellProps) => {
    return <td>{formatDateTime(new Date(props.dataItem.createdDate))}</td>;
  };

  const modifiedDateCell = (props: GridCellProps) => {
    const isLatest = latestHeartbeat?.id === props.dataItem.id;
    return (
      <td className={isLatest ? "fw-bold text-primary" : ""}>
        {formatDateTime(new Date(props.dataItem.modifiedDate))}
        {isLatest && (
          <span
            className="badge bg-success ms-2"
            style={{ fontSize: "0.7rem" }}
          >
            Latest
          </span>
        )}
      </td>
    );
  };

  const availableFilesCell = (props: GridCellProps) => {
    const payload = props.dataItem.payload as HeartbeatPayloadDetailed;
    const count = payload?.hostInfo?.filteredDataFiles?.length || 0;
    return <td className="text-center">{count}</td>;
  };

  const handleRowClick = (e: GridRowClickEvent) => {
    setSelectedHeartbeat(e.dataItem);
  };

  const purgeHeartbeats = async () => {
    setShowSpinner(true);
    try {
      const ok = await service.purgeHeartbeats(id, new Date().toISOString());
      if (!ok) {
        showErrorMessage("Purge of heartbeats failed to execute.");
      } else {
        showSuccessMessage(
          "Purge of heartbeats started, check activity logs for progress.",
        );
        setRefreshKey((prev) => prev + 1);
      }
    } catch (error) {
      showErrorMessage("Error purging heartbeats");
    } finally {
      setShowSpinner(false);
    }
  };

  usePageActions(
    <>
      <BlockButton
        disabled={false}
        showSpinner={showSpinner}
        onClick={purgeHeartbeats}
      >
        <Icon iconName={IconDefinitions.purge} className="me-2" />
        Purge Heartbeats
      </BlockButton>
    </>,
  );

  return (
    <>
      <div className="row">
        {/* Heartbeat History Grid */}
        <div className="col-lg-7 mb-3">
          <TitlelessCard className="p-0" bodyClassName="p-0">
            <div className="px-3 py-2 border-bottom bg-light d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-0">Heartbeat History</h6>
                <small className="text-muted">
                  Click a row to view details • Auto-refreshes every 10s
                </small>
              </div>
              <LiveBadge />
            </div>
            <ODataGrid
              key={refreshKey}
              getData={getData}
              pageSize={10}
              filterable={false}
              sortable={true}
              pageable={{ buttonCount: 5 }}
              sort={[{ field: "modifiedDate", dir: "desc" }]}
              onRowClick={handleRowClick}
            >
              <ResponsiveGridColumn
                title="Last Modified"
                field="modifiedDate"
                sortable={true}
                filter="date"
                cell={modifiedDateCell}
              />
              <ResponsiveGridColumn
                title="Created"
                field="createdDate"
                sortable={true}
                filter="date"
                cell={createdDateCell}
                breakpoint={Breakpoints.md}
              />
              <ResponsiveGridColumn
                title="Files"
                field="payload"
                cell={availableFilesCell}
                width = {80}
              />
            </ODataGrid>
          </TitlelessCard>
        </div>

        {/* Latest Heartbeat Panel */}
        <div className="col-lg-5">
          <TitlelessCard>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h5 className="mb-0">
                {selectedHeartbeat ? "Selected Heartbeat" : "Latest Heartbeat"}
              </h5>
              {selectedHeartbeat && (
                <button
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => setSelectedHeartbeat(null)}
                >
                  <Icon iconName={IconDefinitions.refresh} className="me-1" />
                  View Latest
                </button>
              )}
            </div>
            <HeartbeatDetailsPanel
              heartbeat={selectedHeartbeat || latestHeartbeat}
            />
          </TitlelessCard>
        </div>
      </div>
    </>
  );
}

export default CTEyeClientHeartbeats;
